import { Controller } from "@hotwired/stimulus"

// Wraps the videos search form and, on mobile, the full-screen filter modal.
//
// While the modal is open you can play with the query and filters and watch the
// result + facet counts update live. "Show results" commits that state; "Cancel"
// discards it and returns to where you were when you opened the modal.
//
// Rather than navigate back to a cached URL on cancel — a full server round-trip
// that feels janky next to the live in-modal updates — we snapshot the committed
// state when the modal opens and restore it from the DOM instantly on cancel. No
// network, no flash.
export default class extends Controller {
  static targets = [ 'wrapper' ];

  connect(){
    this.element.modal = this;

    // Arriving from the global header search icon on another page: the link
    // carries ?open_search=1 so we open the modal focused on load (the YouTube
    // pattern — tap search anywhere, land in the search screen). Strip the flag
    // from the URL first so a refresh or Cancel-restore doesn't reopen it.
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_search')) {
      params.delete('open_search');
      const query = params.toString();
      const cleaned = window.location.pathname + (query ? `?${query}` : '');
      window.history.replaceState(window.history.state, '', cleaned);
      this.openModal();
    }
  }

  open(e) {
    e.preventDefault();
    this.openModal();
  }

  openModal() {
    // Always open on the search screen (Screen A), even if the last session
    // committed from the filters screen. Done before snapshotting so the
    // captured HTML — and therefore cancel — also lands on Screen A.
    const screens = this.wrapperTarget.querySelector('[data-controller~="modal-screens"]');
    if (screens) screens.dataset.screen = 'search';
    // Capture the committed state before the user starts exploring. These are
    // exactly the regions the index.turbo_stream response replaces, plus the
    // query value (which the stream never re-renders) and the current URL.
    this.snapshot = {
      wrapperHTML: this.wrapperTarget.innerHTML,
      resultsHTML: this.resultsElement.innerHTML,
      query: this.queryElement ? this.queryElement.value : null,
      url: window.location.href,
    };
    // Live submits during the session should replace (not stack) history, so the
    // back button and cancel both land on the committed URL rather than a
    // half-explored query. search_controller#updateURL reads this flag.
    if (this.formElement) this.formElement.dataset.modalSession = 'true';
    this.wrapperTarget.classList.remove('hidden');

    // Tapping the search bar should land the cursor in the box. Focus
    // synchronously, still inside the tap gesture, so mobile Safari also raises
    // the keyboard (it ignores programmatic focus outside a user gesture). The
    // input is on the now-visible search screen.
    if (this.queryElement) {
      const query = this.queryElement;
      query.focus();
      // Place the caret after any existing text rather than selecting it.
      const end = query.value.length;
      query.setSelectionRange(end, end);
    }
  }

  // "Show results" — keep the live state, just close.
  close() {
    if (this.formElement) delete this.formElement.dataset.modalSession;
    this.wrapperTarget.classList.add('hidden');
  }

  // "Cancel" — restore the committed state captured on open, instantly.
  cancel() {
    if (this.snapshot) {
      this.wrapperTarget.innerHTML = this.snapshot.wrapperHTML;
      this.resultsElement.innerHTML = this.snapshot.resultsHTML;
      // The query field lives in the wrapper but isn't re-rendered by submits,
      // so its value can drift from the restored markup — set it explicitly.
      if (this.queryElement) this.queryElement.value = this.snapshot.query;
      window.history.replaceState(window.history.state, null, this.snapshot.url);
    }
    this.close();
  }

  get formElement() {
    return this.element.querySelector('form');
  }

  get resultsElement() {
    return document.getElementById('search_results');
  }

  get queryElement() {
    return this.element.querySelector('[name="query"]');
  }
}
